import { Link } from 'react-router-dom';
import { Globe, Mail, Share2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { PUBLIC_ROUTES, ROUTES } from '@/constants';
import config from '@/config';
import { ButtonLink } from '@/components/ui/button-link';
import PublicNewsletterCta from './PublicNewsletterCta';

const FOOTER_SECTIONS = [
  {
    title: 'Quick Links',
    links: [
      { label: 'Live Scores', to: PUBLIC_ROUTES.LIVE },
      { label: 'Fixtures', to: PUBLIC_ROUTES.FIXTURES },
      { label: 'Results', to: PUBLIC_ROUTES.RESULTS },
      { label: 'Tournaments', to: PUBLIC_ROUTES.TOURNAMENTS },
    ],
  },
  {
    title: 'Resources',
    links: [
      { label: 'Teams', to: PUBLIC_ROUTES.TEAMS },
      { label: 'Players', to: PUBLIC_ROUTES.PLAYERS },
      { label: 'Grounds', to: PUBLIC_ROUTES.GROUNDS },
      { label: 'Gallery', to: PUBLIC_ROUTES.GALLERY },
      { label: 'Sponsors', to: PUBLIC_ROUTES.SPONSORS },
    ],
  },
  {
    title: 'Support',
    links: [
      { label: 'About', to: PUBLIC_ROUTES.ABOUT },
      { label: 'Contact', to: PUBLIC_ROUTES.CONTACT },
      { label: 'Privacy Policy', to: PUBLIC_ROUTES.PRIVACY },
      { label: 'Terms of Service', to: PUBLIC_ROUTES.TERMS },
      { label: 'Admin Sign In', to: ROUTES.LOGIN },
    ],
  },
];

const SOCIAL = [
  { label: 'Facebook', icon: Share2, href: '#' },
  { label: 'Instagram', icon: Globe, href: '#' },
  { label: 'YouTube', icon: Share2, href: '#' },
];

function PublicFooter({ className }) {
  const year = new Date().getFullYear();

  return (
    <footer className={cn('border-t border-border bg-primary-900 text-white', className)}>
      <div className="vp-container-wide py-10">
        <PublicNewsletterCta className="border-white/10 bg-white/5 text-white [&_h2]:text-white [&_p]:text-white/75" />
      </div>
      <div className="vp-container-wide pb-12 sm:pb-16">
        <div className="grid gap-10 lg:grid-cols-12">
          <div className="space-y-4 lg:col-span-4">
            <Link to={PUBLIC_ROUTES.HOME} className="inline-flex items-center gap-2 font-display text-xl font-extrabold">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-accent-cyan text-sm font-black">
                VP
              </span>
              {config.appName}
            </Link>
            <p className="max-w-sm text-sm leading-relaxed text-white/70">
              Enterprise volleyball tournament platform — live scores, fixtures, standings, analytics, and fan
              experiences built for clubs and organizers.
            </p>
            <div className="flex gap-2">
              {SOCIAL.map(({ label, icon: Icon, href }) => (
                <a
                  key={label}
                  href={href}
                  aria-label={label}
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-white/5 text-white/80 transition-colors hover:border-accent-cyan/40 hover:bg-white/10 hover:text-white"
                >
                  <Icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>

          {FOOTER_SECTIONS.map((section) => (
            <div key={section.title} className="lg:col-span-2">
              <h3 className="font-display text-sm font-bold uppercase tracking-wider text-white/90">{section.title}</h3>
              <ul className="mt-4 space-y-2.5">
                {section.links.map((link) => (
                  <li key={link.to}>
                    <Link to={link.to} className="text-sm text-white/65 transition-colors hover:text-accent-cyan">
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          <div className="lg:col-span-2">
            <h3 className="font-display text-sm font-bold uppercase tracking-wider text-white/90">Contact</h3>
            <p className="mt-4 text-sm text-white/65">
              Questions about tournaments, registration, or partnerships?
            </p>
            <ButtonLink
              to={PUBLIC_ROUTES.CONTACT}
              variant="accent"
              size="sm"
              className="mt-4"
            >
              <Mail className="mr-2 h-4 w-4" aria-hidden="true" />
              Get in touch
            </ButtonLink>
          </div>
        </div>

        <div className="mt-12 flex flex-col gap-3 border-t border-white/10 pt-6 text-sm text-white/50 sm:flex-row sm:items-center sm:justify-between">
          <p>&copy; {year} {config.appName}. All rights reserved.</p>
          <p className="text-xs">Built for volleyball communities worldwide.</p>
        </div>
      </div>
    </footer>
  );
}

export default PublicFooter;
